import { Rocket, Zap, Globe, Eye, Sparkles, Star, Cpu, Shield } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

function Homepage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen gradient-bg relative overflow-hidden">
      {/* Cyber Grid Background */}
      <div className="absolute inset-0 bg-cyber-grid bg-cyber-grid opacity-30 pointer-events-none"></div>
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Hero Icon */}
          <div className="flex justify-center mb-8 animate-fade-in-up">
            <div className="relative p-6 bg-gradient-to-br from-cyan-500/20 to-purple-600/20 backdrop-blur-xl rounded-3xl border border-cyan-500/30 shadow-2xl animate-float">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-600/10 rounded-3xl animate-pulse-glow"></div>
              <Rocket className="w-12 h-12 text-cyan-400 relative z-10 drop-shadow-lg" />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-neon-cyan rounded-full animate-ping"></div>
            </div>
          </div>
          
          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 animate-fade-in-up-delay">
            Generate Your{' '}
            <span className="neon-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent animate-gradient-x">
              Memecoin
            </span>
            <br />
            <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-300 font-normal">
            Website
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto px-4 leading-relaxed animate-fade-in-up-delay">
            Create <span className="text-cyan-400 font-semibold">stunning</span>, 
            <span className="text-purple-400 font-semibold"></span> landing pages for your community coin in 
            <span className="text-blue-400 font-semibold"> minutes</span>. 
            <br className="hidden sm:block" />
            Just enter the details, preview your site, and publish 
            <span className="text-green-400 font-semibold"> instantly</span> no coding required.
          </p>
          
          {/* CTA Button */}
          <div className="flex justify-center mb-12 animate-fade-in-up-delay" style={{animationDelay: '0.3s'}}>
            <button 
              onClick={() => navigate('/generate')}
              className="group relative overflow-hidden bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 text-white font-bold py-4 px-8 rounded-2xl shadow-2xl transition-all duration-300 transform hover:scale-105 focus:outline-none text-lg"
              style={{
                boxShadow: '0 0 30px rgba(6, 182, 212, 0.4), 0 8px 25px rgba(0, 0, 0, 0.3)',
                background: 'linear-gradient(45deg, #06b6d4, #3b82f6, #8b5cf6, #06b6d4)',
                backgroundSize: '400% 400%',
                animation: 'gradient-shift 3s ease infinite'
              }}
            >
              <span className="relative z-10">
                Start Building Now
              </span>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 -top-2 -bottom-2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-600/20 blur-xl group-hover:blur-2xl transition-all duration-300"></div>
            </button>
          </div>
          
          {/* Feature Pills */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-16 px-4">
            <div className="glass-card px-4 py-3 flex items-center gap-2 text-cyan-300 hover:bg-cyan-500/10 transition-all duration-300 hover:scale-105 animate-slide-in-left">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
              <span className="text-sm sm:text-base font-medium">Lightning Fast</span>
            </div>
            <div className="glass-card px-4 py-3 flex items-center gap-2 text-purple-300 hover:bg-purple-500/10 transition-all duration-300 hover:scale-105 animate-slide-in-left" style={{animationDelay: '0.1s'}}>
              <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
              <span className="text-sm sm:text-base font-medium">Custom Domain</span>
            </div>
            <div className="glass-card px-4 py-3 flex items-center gap-2 text-blue-300 hover:bg-blue-500/10 transition-all duration-300 hover:scale-105 animate-slide-in-right" style={{animationDelay: '0.2s'}}>
              <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              <span className="text-sm sm:text-base font-medium">Live Preview</span>
            </div>
            <div className="glass-card px-4 py-3 flex items-center gap-2 text-green-300 hover:bg-green-500/10 transition-all duration-300 hover:scale-105 animate-slide-in-right" style={{animationDelay: '0.3s'}}>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              <span className="text-sm sm:text-base font-medium">AI Powered</span>
            </div>
            <div className="glass-card px-4 py-3 flex items-center gap-2 text-red-300 hover:bg-red-500/10 transition-all duration-300 hover:scale-105 animate-slide-in-right" style={{animationDelay: '0.4s'}}>
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
              <span className="text-sm sm:text-base font-medium">Secure</span>
            </div>
            <div className="glass-card px-4 py-3 flex items-center gap-2 text-indigo-300 hover:bg-indigo-500/10 transition-all duration-300 hover:scale-105 animate-slide-in-right" style={{animationDelay: '0.5s'}}>
              <Cpu className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
              <span className="text-sm sm:text-base font-medium">Optimized</span>
            </div>
          </div>

          {/* Stats or Additional Info */}
          <div className="flex flex-wrap justify-center gap-8 sm:gap-12 text-center mb-8">
            <div className="animate-fade-in-up-delay">
              <div className="text-2xl sm:text-3xl font-bold text-cyan-400 neon-text">10,000+</div>
              <div className="text-sm text-gray-400">Sites Created</div>
            </div>
            <div className="animate-fade-in-up-delay" style={{animationDelay: '0.2s'}}>
              <div className="text-2xl sm:text-3xl font-bold text-purple-400 neon-text">{"< 5 Min"}</div>
              <div className="text-sm text-gray-400">Average Setup</div>
            </div>
            <div className="animate-fade-in-up-delay" style={{animationDelay: '0.4s'}}>
              <div className="text-2xl sm:text-3xl font-bold text-green-400 neon-text">99.9%</div>
              <div className="text-sm text-gray-400">Uptime</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Homepage 